import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { ChallengeService, FirebaseService } from '@fitzaa/shared';
import { ChallengeWithEligibility, User as SharedUser } from '@fitzaa/shared';
import { firebaseConfig, getEnvironment } from '../config/firebase';
import { joinChallenge } from '../services/WebCloudFunctionsService';
import Header from '../components/Header';
import ChallengeCard from '../components/ChallengeCard';

type ChallengeFilter = 'all' | 'global' | 'personal' | 'organization';
type SortOption = 'newest' | 'popular' | 'ending_soon' | 'points_high';

const JoinChallengeScreen: React.FC = () => {
    const { user } = useAuth();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [allChallenges, setAllChallenges] = useState<ChallengeWithEligibility[]>([]);
    const [userParticipations, setUserParticipations] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedFilter, setSelectedFilter] = useState<ChallengeFilter>('all');
    const [selectedSort, setSelectedSort] = useState<SortOption>('newest');
    const [searchQuery, setSearchQuery] = useState('');

    const convertToSharedUser = (webUser: any): SharedUser => {
        const userData = webUser.profile ? { ...webUser, ...webUser.profile } : webUser;

        return {
            id: userData.id,
            email: userData.email || '',
            displayName: userData.displayName || '',
            profileImageURL: userData.photoURL || undefined,
            joinedDate: new Date().toISOString(),
            role: userData.role || 'member',
            streak: 0,
            totalPoints: 0,
            organizationId: userData.organizationId,
            organizationName: userData.organizationName,
            teamId: userData.teamId,
            teamName: userData.teamName,
            authProvider: (userData.authProvider === 'azure' ? 'azure' :
                userData.authProvider === 'google' ? 'google' : 'email') as 'azure' | 'google' | 'email',
        };
    };

    const fetchChallenges = useCallback(async () => {
        if (!user?.id) return;

        try {
            setLoading(true);
            setError(null);
            const sharedUser = convertToSharedUser(user);

            const firebaseService = new FirebaseService(firebaseConfig, getEnvironment());
            const challengeService = new ChallengeService(firebaseService);

            // Fetch both challenges and participations in parallel
            const [challengesData, participations] = await Promise.all([
                challengeService.getAvailableChallenges(user.id, sharedUser),
                firebaseService.loadUserParticipations(user.id, sharedUser)
            ]);

            console.log('📊 Fetched challenges for join:', challengesData.length);
            console.log('📊 User participations:', participations.length);
            
            setAllChallenges(challengesData);
            setUserParticipations(participations);
        } catch (err: any) {
            console.error('❌ Error fetching challenges:', err);
            setError(err.message || 'Failed to load challenges');
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchChallenges();
    }, [fetchChallenges]);

    const handleJoinChallenge = async (challengeId: string) => {
        if (!user?.id) {
            alert('You must be logged in to join challenges');
            return;
        }

        try {
            console.log('🔄 Joining challenge:', challengeId);
            const result = await joinChallenge(challengeId) as any;

            if (result.success) {
                console.log('✅ Successfully joined challenge:', challengeId);

                // Force refresh challenges to update the lists after joining
                // Note: This screen doesn't have fetchChallenges with force parameter,
                // but navigating away will trigger HomeScreen to refresh
                await fetchChallenges();

                // Navigate to challenge progress screen instead of showing popup
                navigate(`/challenge-progress/${challengeId}`);
            } else {
                console.error('❌ Failed to join challenge:', result.error);
                alert(result.error || 'Failed to join challenge');
            }
        } catch (error: any) {
            console.error('❌ Error joining challenge:', error);
            const errorMessage = error.message || 'Failed to join challenge. Please try again.';
            alert(errorMessage);
        }
    };

    // Filter and sort challenges
    const filteredAndSortedChallenges = useMemo(() => {
        // Get set of challenge IDs the user has already joined
        const joinedChallengeIds = new Set(
            userParticipations
                .filter(p => p.status === 'in_progress' || p.status === 'completed' || p.isActive === true)
                .map(p => p.challengeId)
        );

        let filtered = allChallenges.filter(challenge => {
            // Filter out challenges the user has already joined
            if (joinedChallengeIds.has(challenge.id)) {
                return false;
            }

            // Hide expired challenges
            const now = new Date();
            const endDate = new Date(challenge.endDate);
            if (now > endDate) {
                return false;
            }

            // Apply search filter
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const matchesSearch =
                    challenge.title?.toLowerCase().includes(query) ||
                    challenge.description?.toLowerCase().includes(query) ||
                    challenge.category?.toLowerCase().includes(query);
                if (!matchesSearch) return false;
            }

            // Apply category filter
            switch (selectedFilter) {
                case 'global':
                    return challenge.level === 'global';
                case 'personal':
                    return challenge.level === 'personal';
                case 'organization':
                    return challenge.level === 'organization';
                default:
                    return true;
            }
        });

        // Apply sorting
        filtered.sort((a, b) => {
            switch (selectedSort) {
                case 'newest':
                    return new Date(b.startDate).getTime() - new Date(a.startDate).getTime();
                case 'popular':
                    return (b.points || 0) - (a.points || 0);
                case 'ending_soon':
                    return new Date(a.endDate).getTime() - new Date(b.endDate).getTime();
                case 'points_high':
                    return (b.points || 0) - (a.points || 0);
                default:
                    return 0;
            }
        });

        return filtered;
    }, [allChallenges, userParticipations, selectedFilter, selectedSort, searchQuery]);

    // Check if user belongs to an organization
    const hasOrganization = useMemo(() => {
        return !!(user?.organizationId || user?.organizationName);
    }, [user]);

    const getFilterIcon = (filter: ChallengeFilter) => {
        switch (filter) {
            case 'global': return '🌍';
            case 'personal': return '👤';
            case 'organization': return '🏢';
            default: return '🎯';
        }
    };

    const getSortIcon = (sort: SortOption) => {
        switch (sort) {
            case 'newest': return '🆕';
            case 'popular': return '🔥';
            case 'ending_soon': return '⏰';
            case 'points_high': return '⭐';
            default: return '📊';
        }
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: theme.colors.background,
                color: theme.colors.text
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '16px' }}>Loading...</div>
                    <div>Loading amazing challenges...</div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: theme.colors.background,
                color: theme.colors.text
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '24px', marginBottom: '16px', color: theme.colors.error }}>Error</div>
                    <div>{error}</div>
                    <button
                        onClick={fetchChallenges}
                        style={{
                            marginTop: '16px',
                            padding: '8px 16px',
                            backgroundColor: theme.colors.primary,
                            color: theme.colors.primaryText,
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: theme.colors.background,
            color: theme.colors.text,
            fontFamily: theme.fonts.primary
        }}>
            {/* Header */}
            <Header
                title="Join Challenges"
                showBack={true}
                showMenu={false}
                showProfile={true}
            />

            {/* Main Content */}
            <main style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
                {/* Search Bar */}
                <div style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: '16px',
                    padding: '20px',
                    marginBottom: '24px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    border: `1px solid ${theme.colors.border}`,
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        marginBottom: '16px',
                    }}>
                        <span style={{ fontSize: '20px' }}>🔍</span>
                        <input
                            type="text"
                            placeholder="Search challenges by title, description, or category..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                flex: 1,
                                padding: '12px 16px',
                                border: `1px solid ${theme.colors.border}`,
                                borderRadius: '8px',
                                fontSize: '16px',
                                fontFamily: theme.fonts.primary,
                                backgroundColor: theme.colors.background,
                                color: theme.colors.text,
                            }}
                        />
                    </div>
                </div>

                {/* Filter Tabs */}
                <div style={{ marginBottom: '24px' }}>
                    <div style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '12px',
                        marginBottom: '16px',
                    }}>
                        {(['all', 'global', 'personal', ...(hasOrganization ? ['organization'] : [])] as ChallengeFilter[]).map((filter) => {
                            const isSelected = selectedFilter === filter;
                            const getTagColors = (f: ChallengeFilter) => {
                                if (f === 'global') return { bg: 'rgba(59, 130, 246, 0.12)', color: '#2563eb' };
                                if (f === 'organization') return { bg: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' };
                                if (f === 'personal') return { bg: 'rgba(139, 92, 246, 0.14)', color: '#7c3aed' };
                                return { bg: 'rgba(102, 126, 234, 0.12)', color: '#667eea' };
                            };
                            const tagColors = getTagColors(filter);
                            
                            return (
                                <button
                                    key={filter}
                                    onClick={() => setSelectedFilter(filter)}
                                    style={{
                                        padding: '8px 16px',
                                        borderRadius: '999px',
                                        border: isSelected ? `2px solid ${tagColors.color}` : `2px solid ${theme.colors.border}`,
                                        backgroundColor: isSelected ? tagColors.bg : theme.colors.surface,
                                        color: isSelected ? tagColors.color : theme.colors.text,
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        fontFamily: theme.fonts.secondary,
                                        letterSpacing: '0.5px',
                                        textTransform: 'capitalize',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) {
                                            e.currentTarget.style.backgroundColor = tagColors.bg;
                                            e.currentTarget.style.borderColor = tagColors.color;
                                            e.currentTarget.style.color = tagColors.color;
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSelected) {
                                            e.currentTarget.style.backgroundColor = theme.colors.surface;
                                            e.currentTarget.style.borderColor = theme.colors.border;
                                            e.currentTarget.style.color = theme.colors.text;
                                        }
                                    }}
                                >
                                    {getFilterIcon(filter)} {filter.charAt(0).toUpperCase() + filter.slice(1)}
                                </button>
                            );
                        })}
                    </div>

                    {/* Sort Options */}
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        flexWrap: 'wrap',
                    }}>
                        <span style={{
                            fontSize: '14px',
                            fontWeight: '500',
                            color: theme.colors.textSecondary,
                        }}>
                            Sort by:
                        </span>
                        {(['newest', 'popular', 'ending_soon', 'points_high'] as SortOption[]).map((sort) => {
                            const isSelected = selectedSort === sort;
                            const getSortColors = (s: SortOption) => {
                                if (s === 'popular' || s === 'points_high') return { bg: 'rgba(239, 68, 68, 0.12)', color: '#ef4444' };
                                if (s === 'ending_soon') return { bg: 'rgba(59, 130, 246, 0.12)', color: '#2563eb' };
                                return { bg: 'rgba(102, 126, 234, 0.12)', color: '#667eea' };
                            };
                            const sortColors = getSortColors(sort);
                            
                            return (
                                <button
                                    key={sort}
                                    onClick={() => setSelectedSort(sort)}
                                    style={{
                                        padding: '6px 14px',
                                        borderRadius: '999px',
                                        border: isSelected ? `2px solid ${sortColors.color}` : `2px solid ${theme.colors.border}`,
                                        backgroundColor: isSelected ? sortColors.bg : theme.colors.surface,
                                        color: isSelected ? sortColors.color : theme.colors.text,
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        fontFamily: theme.fonts.secondary,
                                        letterSpacing: '0.5px',
                                        textTransform: 'capitalize',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isSelected) {
                                            e.currentTarget.style.backgroundColor = sortColors.bg;
                                            e.currentTarget.style.borderColor = sortColors.color;
                                            e.currentTarget.style.color = sortColors.color;
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isSelected) {
                                            e.currentTarget.style.backgroundColor = theme.colors.surface;
                                            e.currentTarget.style.borderColor = theme.colors.border;
                                            e.currentTarget.style.color = theme.colors.text;
                                        }
                                    }}
                                >
                                    {getSortIcon(sort)} {sort.replace('_', ' ')}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Challenges Grid */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                    gap: '24px',
                }}>
                    {filteredAndSortedChallenges.length > 0 ? (
                        filteredAndSortedChallenges.map((challenge) => (
                            <ChallengeCard
                                key={challenge.id}
                                challenge={challenge}
                                onJoin={handleJoinChallenge}
                                isActive={false}
                            />
                        ))
                    ) : (
                        <div style={{
                            gridColumn: '1 / -1',
                            textAlign: 'center',
                            padding: '80px 24px',
                            backgroundColor: theme.colors.surface,
                            borderRadius: '20px',
                            border: `1px solid ${theme.colors.border}`,
                        }}>
                            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🎯</div>
                            <h3 style={{
                                fontSize: '24px',
                                fontWeight: '600',
                                color: theme.colors.textSecondary,
                                margin: '0 0 12px 0',
                                fontFamily: theme.fonts.display,
                            }}>
                                No challenges found
                            </h3>
                            <p style={{
                                fontSize: '16px',
                                color: theme.colors.textTertiary,
                                margin: 0,
                                lineHeight: '1.5',
                                maxWidth: '400px',
                                marginLeft: 'auto',
                                marginRight: 'auto',
                            }}>
                                {searchQuery
                                    ? `No challenges match "${searchQuery}"`
                                    : 'Try adjusting your filters or check back later for new challenges'
                                }
                            </p>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default JoinChallengeScreen; 